import type { StateCreatorEnhance } from '@halo/zustand-enhance';
import { createEnhance } from '@halo/zustand-enhance';
import {
  setLocale,
  formatTimeDisplay,
  formatTimeSimpleDisplay,
  type FormatTimeDisplayResult,
} from '@halo/utils';
import { useAppStore } from '@halo/chat-sdk-react';
import type { LanguageListKeys } from '@/configs/i18n';
import {
  defaultLanguage,
  languageList,
  type LanguageItem,
  type LanguageList,
} from '@/configs/i18n';
import { getUserLanguage, i18n, setUserLanguage } from './i18n';

export interface I18nStore {
  language: LanguageListKeys;
  languageItem: LanguageItem;
  languageList: LanguageList;
  setLanguage: typeof i18n.changeLanguage;
  formatTime: (timestamp: number) => FormatTimeDisplayResult;
  formatTimeSimple: (timestamp: number) => string;
}

const setLanguage: I18nStore['setLanguage'] = (language: LanguageListKeys, callback) => {
  setUserLanguage(language);
  useI18nStore.setState({ language });
  return i18n.changeLanguage(language, callback);
};

function getTimeFormatters() {
  const serverTime = useAppStore.getState().client?.serverTime?.time || Date.now();
  return {
    formatTime: (timestamp: number) => formatTimeDisplay(timestamp, serverTime),
    formatTimeSimple: (timestamp: number) => formatTimeSimpleDisplay(timestamp, serverTime),
  };
}

const createI18nStore: StateCreatorEnhance<I18nStore> = () => {
  const language = getUserLanguage();
  const languageItem =
    languageList.findByKey(language) || languageList.findByKey(defaultLanguage)!;
  setLocale(languageItem.dayjsKey);
  return {
    language,
    languageItem,
    languageList,
    setLanguage,
    ...getTimeFormatters(),
  };
};

export const useI18nStore = createEnhance(createI18nStore);

// Compute the languageItem
useI18nStore.subscribe(
  (state) => state.language,
  (language) => {
    useI18nStore.setState({
      languageItem: languageList.findByKey(language) || languageList.findByKey(defaultLanguage)!,
    });
  }
);

// Set the dayjs locale
useI18nStore.subscribe(
  (state) => state.languageItem.dayjsKey,
  (dayjsKey) => {
    setLocale(dayjsKey);
    useI18nStore.setState(getTimeFormatters());
  }
);

useAppStore.subscribe(
  (state) => state.initTrigger,
  (initTrigger) => {
    if (initTrigger) {
      useI18nStore.setState(getTimeFormatters());
      useAppStore.getState().client?.serverTime.addEventListener('sync', () => {
        useI18nStore.setState(getTimeFormatters());
      });
    }
  }
);
